import { z } from "zod";
import {
  DEFAULT_LANGUAGE,
  PUBLIC_INAPP_LANGUAGES,
  SUPPORTED_LANGUAGES,
} from "../../../config/i18n";
import { SupportedLanguage } from "../../../interfaces";
import type { RoomContentLangSelection } from "../stores/roomContentLangStore";
import { localStore } from "../../../lib/store";
import type { CurrentUser } from "../../user/hooks/useCurrentUser";
import { DebugLogger } from "./debugLogger";

const supportedLanguageSchema = z.enum(SUPPORTED_LANGUAGES);

const publicAppLangStore = localStore<SupportedLanguage | null>(
  "publicAppLang",
  null,
  supportedLanguageSchema.nullable()
);

const roomLangsStore = localStore<Record<string, SupportedLanguage>>(
  "roomLangs",
  {},
  z.record(z.string(), supportedLanguageSchema)
);

/** Parse a string into a SupportedLanguage, returning undefined if invalid. */
export function parseSupportedLanguage(
  value: string
): SupportedLanguage | undefined {
  const result = supportedLanguageSchema.safeParse(value);
  return result.success ? result.data : undefined;
}

const debug = new DebugLogger("LangUtils");
debug.mute();

/**
 * Normalize a language string to a SupportedLanguage, defaulting to DEFAULT_LANGUAGE if invalid
 */
export function normalizeLanguage(
  language: string | null | undefined
): SupportedLanguage {
  if (language === null || language === undefined || language === "")
    return DEFAULT_LANGUAGE;
  return (
    parseSupportedLanguage(language.toLowerCase().slice(0, 2)) ??
    DEFAULT_LANGUAGE
  );
}

export function getStoredPublicAppLang(): SupportedLanguage | null {
  return publicAppLangStore.get();
}

export function getStoredRoomLang(roomId: string): SupportedLanguage | null {
  const lang = roomLangsStore.get()[roomId];
  return lang === undefined ? null : lang;
}

/**
 * Get stored room language from user settings (API) with localStorage fallback
 * Auto-migrates localStorage data to API when found
 */
export async function getStoredRoomLangFromUser(
  roomId: string,
  currentUser: CurrentUser | null
): Promise<SupportedLanguage | null> {
  if (currentUser?.roomUserSettings) {
    const roomSettings = currentUser.roomUserSettings.find(
      (settings) => settings.roomId === roomId
    );
    if (roomSettings?.language) {
      return roomSettings.language;
    }
  }

  const localStorageLang = getStoredRoomLang(roomId);
  if (localStorageLang && currentUser) {
    try {
      const { apiClient } = await import("../../../services/api");
      await apiClient.patch("/webapp/private/users/room/:roomId/settings", {
        param: { roomId },
        json: { language: localStorageLang },
      });

      cleanStoredRoomLang(roomId);
    } catch {
      // api error already captured by onRequestError.
    }
  }

  return localStorageLang;
}

export function determineInitialPublicAppLang(): SupportedLanguage {
  const storedLang = getStoredPublicAppLang();
  if (storedLang !== null && PUBLIC_INAPP_LANGUAGES.includes(storedLang)) {
    return storedLang;
  }

  const navigatorLang = parseSupportedLanguage(navigator.language.slice(0, 2));
  if (
    navigatorLang !== undefined &&
    PUBLIC_INAPP_LANGUAGES.includes(navigatorLang)
  ) {
    return navigatorLang;
  }

  return DEFAULT_LANGUAGE;
}

export function storePublicLang(lang: SupportedLanguage) {
  debug.log("Storing public lang", { lang });
  publicAppLangStore.set(lang);
}

export function storeRoomLang(roomId: string, lang: SupportedLanguage) {
  debug.log("Storing room lang", { lang });
  roomLangsStore.set((prev) => ({ ...prev, [roomId]: lang }));
}

/**
 * Clean stored room language after a successful API save.
 */
export function cleanStoredRoomLang(roomId: string) {
  roomLangsStore.set((prev) => {
    if (!Object.hasOwn(prev, roomId)) return prev;
    const { [roomId]: _, ...rest } = prev;
    return rest;
  });
}

export function getUserLocaleIfAvailable(
  availableLangs: SupportedLanguage[]
): SupportedLanguage | null {
  const navigatorLang = parseSupportedLanguage(navigator.language.slice(0, 2));
  if (navigatorLang === undefined) return null;
  return availableLangs.includes(navigatorLang) ? navigatorLang : null;
}

/**
 * Resolves which language to display in a room on initial load.
 *
 * Priority: room override → user's app language → first allowed language.
 *
 * `isEphemeral` signals whether the result comes from the user's app language
 * (true) or from an explicit room override / fallback (false).
 */
export function resolveRoomLanguage(params: {
  userLanguage: SupportedLanguage;
  allowedLanguages: ReadonlyArray<SupportedLanguage>;
  roomOverride: SupportedLanguage | null;
}): { language: SupportedLanguage; isEphemeral: boolean } {
  if (
    params.roomOverride !== null &&
    params.allowedLanguages.includes(params.roomOverride)
  ) {
    return { language: params.roomOverride, isEphemeral: false };
  }
  if (params.allowedLanguages.includes(params.userLanguage)) {
    return { language: params.userLanguage, isEphemeral: true };
  }
  const firstAllowed = params.allowedLanguages[0];
  return {
    language: firstAllowed !== undefined ? firstAllowed : DEFAULT_LANGUAGE,
    isEphemeral: false,
  };
}

/**
 * Which of the current room's eight translation strings to display.
 *
 * The reader's explicit in-room language choice if one is set, otherwise their
 * interface locale. NEVER a guess — that is the whole point, and the reason this is
 * not `contentLang`: that one falls back to fr/en because its consumers pass it to
 * the api as a content corpus filter, and for eight strings on one record the
 * fallback silently serves English to de/es/it/pt/ar/nl readers.
 *
 * Extracted as a pure function so the derivation is testable without mounting the
 * hook — the bug it fixes was in the derivation, not in any component.
 */
export function resolveRoomTextLang(
  selection: RoomContentLangSelection | null,
  activeLang: SupportedLanguage
): SupportedLanguage {
  // Only an EXPLICIT choice relabels the room. A "resolved" default came from
  // `allowedLanguages[0]` — admin tag order, not a decision — and must leave the
  // reader on their interface locale so the English fallback still applies.
  return selection !== null && selection.source === "explicit"
    ? selection.language
    : activeLang;
}
